import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import jsPDF from 'jspdf';
import './WeeklyCommercialization.css';

const getLogoBase64 = () =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = '/images/LogoBanaprecios.png';
  });

const svgToDataUrl = (containerEl) =>
  new Promise((resolve) => {
    const svgEl = containerEl?.querySelector('svg');
    if (!svgEl) { resolve(null); return; }
    const { width, height } = svgEl.getBoundingClientRect();
    if (!width || !height) { resolve(null); return; }
    const clone = svgEl.cloneNode(true);
    clone.setAttribute('width', width);
    clone.setAttribute('height', height);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    const svgStr = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const scale = 2;
      const canvas = document.createElement('canvas');
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve({ dataUrl: canvas.toDataURL('image/png'), width, height });
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });

const API = process.env.REACT_APP_API_URL;

const LOAD_TYPES = [
  { value: 'contenedor_paletizado', label: 'Contenedor Paletizado' },
  { value: 'contenedor_granel', label: 'Contenedor Granel' },
  { value: 'paletizado_bajo_cubierta', label: 'Paletizado Bajo Cubierta' },
  { value: 'granel_bajo_cubierta', label: 'Granel Bajo Cubierta' },
];

const DAY_NAMES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function toISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDate(isoStr) {
  const [y, m, d] = isoStr.split('-');
  return `${d}/${m}/${y}`;
}

function formatCurrency(val) {
  return parseFloat(val || 0).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function getISOWeek(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

const EMPTY_FORM = {
  date: '',
  producer: '',
  producer_name: '',
  marca: '',
  load_type: '',
  boxes: '',
  negotiated_price: '',
  discounts: '0',
  notes: '',
};

export default function WeeklyCommercialization() {
  const { t } = useTranslation();
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [entries, setEntries] = useState([]);
  const [producers, setProducers] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [sharedWithMe, setSharedWithMe] = useState([]);
  const [viewingOwner, setViewingOwner] = useState(null); // null = mi área
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formDay, setFormDay] = useState('');
  const [marcaMode, setMarcaMode] = useState('select'); // 'select' | 'new'
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  const [showSharePanel, setShowSharePanel] = useState(false);
  const [shareUsername, setShareUsername] = useState('');
  const [shareMsg, setShareMsg] = useState('');
  const [followingSuggestions, setFollowingSuggestions] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const shareDebounceRef = useRef(null);

  const [showExport, setShowExport] = useState(false);
  const [exportStart, setExportStart] = useState('');
  const [exportEnd, setExportEnd] = useState('');

  const [showManage, setShowManage] = useState(false);
  const [editingProducer, setEditingProducer] = useState(null); // { id, name }
  const [editingMarca, setEditingMarca] = useState(null);       // { id, name }

  const barChartRef = useRef(null);
  const lineChartRef = useRef(null);

  const isReadOnly = viewingOwner !== null;

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { week_start: toISO(weekStart) };
      if (viewingOwner) params.owner = viewingOwner;
      const res = await axios.get(`${API}/commercialization/entries/`, { params });
      setEntries(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [weekStart, viewingOwner]);

  const fetchProducers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/commercialization/producers/`);
      setProducers(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchMarcas = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/commercialization/marcas/`);
      setMarcas(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchSharedUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/commercialization/shared/`);
      setSharedUsers(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchSharedWithMe = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/commercialization/shared-with-me/`);
      setSharedWithMe(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  useEffect(() => { fetchProducers(); fetchMarcas(); fetchSharedUsers(); fetchSharedWithMe(); }, [fetchProducers, fetchMarcas, fetchSharedUsers, fetchSharedWithMe]);

  const prevWeek = () => setWeekStart(addDays(weekStart, -7));
  const nextWeek = () => setWeekStart(addDays(weekStart, 7));
  const currentWeek = () => setWeekStart(getMonday(new Date()));

  const weekEnd = addDays(weekStart, 6);
  const weekNumber = getISOWeek(weekStart);
  const weekLabel = `${formatDate(toISO(weekStart))} – ${formatDate(toISO(weekEnd))}`;

  const calcTotal = () => {
    const boxes = parseFloat(form.boxes) || 0;
    const price = parseFloat(form.negotiated_price) || 0;
    const disc = parseFloat(form.discounts) || 0;
    const t = boxes * price - disc;
    return t < 0 ? 0 : t;
  };

  const openNew = (dayISO) => {
    setEditEntry(null);
    setForm({ ...EMPTY_FORM, date: dayISO });
    setFormDay(dayISO);
    setPhotoFile(null);
    setMarcaMode('select');
    setShowModal(true);
  };

  const openEdit = (entry) => {
    setEditEntry(entry);
    setForm({
      date: entry.date,
      producer: entry.producer || '',
      producer_name: entry.producer_display || '',
      marca: entry.marca || '',
      load_type: entry.load_type || '',
      boxes: entry.boxes || '',
      negotiated_price: entry.negotiated_price || '',
      discounts: entry.discounts || '0',
      notes: entry.notes || '',
    });
    setFormDay(entry.date);
    setPhotoFile(null);
    setMarcaMode('select');
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditEntry(null); setForm(EMPTY_FORM); setMarcaMode('select'); };

  const handleProducerSelect = (e) => {
    const val = e.target.value;
    if (val === '__new__') {
      setForm(f => ({ ...f, producer: '', producer_name: '' }));
    } else {
      const prod = producers.find(p => String(p.id) === val);
      setForm(f => ({ ...f, producer: val, producer_name: prod ? prod.name : '' }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        marca: form.marca,
        load_type: form.load_type,
        boxes: form.boxes || 0,
        negotiated_price: form.negotiated_price || 0,
        discounts: form.discounts || 0,
        notes: form.notes,
      };
      if (form.producer) {
        payload.producer = form.producer;
      } else {
        payload.producer_name = form.producer_name;
      }

      let savedEntry;
      if (editEntry) {
        const res = await axios.patch(`${API}/commercialization/entries/${editEntry.id}/`, payload);
        savedEntry = res.data;
      } else {
        const res = await axios.post(`${API}/commercialization/entries/`, payload);
        savedEntry = res.data;
      }

      if (photoFile && savedEntry?.id) {
        setPhotoUploading(true);
        const fd = new FormData();
        fd.append('photo', photoFile);
        await axios.post(`${API}/commercialization/entries/${savedEntry.id}/photo/`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setPhotoUploading(false);
      }

      await fetchEntries();
      await fetchProducers();
      await fetchMarcas();
      closeModal();
    } catch (err) {
      console.error(err);
      alert('Error al guardar la entrada.');
    } finally {
      setSaving(false);
      setPhotoUploading(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('¿Eliminar esta entrada?')) return;
    try {
      await axios.delete(`${API}/commercialization/entries/${entryId}/`);
      setEntries(prev => prev.filter(e => e.id !== entryId));
    } catch (err) {
      alert('Error al eliminar.');
    }
  };

  const fetchFollowingSuggestions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/me/following/`);
      setFollowingSuggestions(r.data);
    } catch {}
  }, []);

  const searchUsers = useCallback(async (q) => {
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const r = await axios.get(`${API}/users/search/?q=${encodeURIComponent(q)}`);
      setSearchResults(r.data);
    } catch {
      setSearchResults([]);
    }
  }, []);

  const handleShare = async (e) => {
    e.preventDefault();
    setShareMsg('');
    setShowSuggestions(false);
    try {
      await axios.post(`${API}/commercialization/shared/`, { username: shareUsername });
      setShareMsg('Acceso concedido correctamente.');
      setShareUsername('');
      fetchSharedUsers();
    } catch (err) {
      setShareMsg(err.response?.data?.error || 'Error al compartir.');
    }
  };

  const handleRevokeShare = async (id) => {
    if (!window.confirm('¿Revocar acceso a este usuario?')) return;
    try {
      await axios.delete(`${API}/commercialization/shared/${id}/`);
      setSharedUsers(prev => prev.filter(u => u.id !== id));
    } catch (err) {
      alert('Error al revocar acceso.');
    }
  };

  const handleRenameProducer = async (id, name) => {
    try {
      await axios.patch(`${API}/commercialization/producers/${id}/`, { name });
      setEditingProducer(null);
      fetchProducers();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al renombrar productor.');
    }
  };

  const handleDeleteProducer = async (id) => {
    if (!window.confirm('¿Eliminar este productor? Las entradas existentes no se borrarán.')) return;
    try {
      await axios.delete(`${API}/commercialization/producers/${id}/`);
      fetchProducers();
    } catch (err) {
      alert('Error al eliminar productor.');
    }
  };

  const handleRenameMarca = async (id, name) => {
    try {
      await axios.patch(`${API}/commercialization/marcas/${id}/`, { name });
      setEditingMarca(null);
      fetchMarcas();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al renombrar marca.');
    }
  };

  const handleDeleteMarca = async (id) => {
    if (!window.confirm('¿Eliminar esta marca? Las entradas existentes no se borrarán.')) return;
    try {
      await axios.delete(`${API}/commercialization/marcas/${id}/`);
      fetchMarcas();
    } catch (err) {
      alert('Error al eliminar marca.');
    }
  };

  const handleExport = async (format) => {
    if (format === 'pdf') {
      await handleExportPDF();
      return;
    }
    // Excel: server-side
    try {
      const params = new URLSearchParams();
      if (viewingOwner) params.set('owner', viewingOwner);
      if (exportStart && exportEnd) {
        params.set('start_date', exportStart);
        params.set('end_date', exportEnd);
      } else {
        params.set('week_start', toISO(weekStart));
      }
      const token = localStorage.getItem('access_token');
      const res = await axios.get(`${API}/commercialization/export/excel/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'comercializacion.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error al exportar Excel.');
    }
  };

  const handleExportPDF = async () => {
    const filteredEntries = (exportStart && exportEnd)
      ? entries.filter(e => e.date >= exportStart && e.date <= exportEnd)
      : entries;

    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const W = pdf.internal.pageSize.getWidth();   // ~841
    const H = pdf.internal.pageSize.getHeight();  // ~595
    const ML = 40; // margin left
    const MR = 40; // margin right
    const CW = W - ML - MR;
    let y = ML;

    const checkPage = (needed) => {
      if (y + needed > H - 30) {
        pdf.addPage();
        y = ML;
      }
    };

    // ── Logo + header ──────────────────────────────────────────────
    const logo = await getLogoBase64();
    if (logo) {
      pdf.addImage(logo, 'PNG', ML, y, 36, 36);
    }
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.setTextColor(23, 198, 113);
    pdf.text('Reporte de Comercialización Semanal', ML + (logo ? 46 : 0), y + 14);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    const periodLabel = (exportStart && exportEnd)
      ? `Período: ${formatDate(exportStart)} al ${formatDate(exportEnd)}`
      : `Semana ${weekNumber}: ${weekLabel}`;
    const now = new Date();
    const exportDateStr = `${String(now.getDate()).padStart(2,'0')}/${String(now.getMonth()+1).padStart(2,'0')}/${now.getFullYear()} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    pdf.text(`${periodLabel}   |   Generado: ${exportDateStr}`, ML + (logo ? 46 : 0), y + 28);

    y += 50;
    pdf.setDrawColor(23, 198, 113);
    pdf.setLineWidth(1.5);
    pdf.line(ML, y, W - MR, y);
    y += 14;

    // ── Totals summary bar ──────────────────────────────────────────
    const pppWeekVal = totalCajas > 0
      ? filteredEntries.reduce((s, e) => s + parseFloat(e.boxes || 0) * parseFloat(e.negotiated_price || 0), 0) / filteredEntries.reduce((s, e) => s + parseFloat(e.boxes || 0), 0)
      : null;
    pdf.setFillColor(240, 253, 244);
    pdf.setDrawColor(23, 198, 113);
    pdf.setLineWidth(0.5);
    pdf.roundedRect(ML, y, CW, 24, 4, 4, 'FD');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(23, 198, 113);
    pdf.text(`Total cajas: ${parseFloat(totalCajas).toLocaleString('es-EC', { minimumFractionDigits: 2 })}`, ML + 10, y + 15);
    pdf.text(`Total monto: $${parseFloat(totalMonto).toLocaleString('es-EC', { minimumFractionDigits: 2 })}`, ML + 200, y + 15);
    if (pppWeekVal !== null) {
      pdf.text(`PPP semana: $${pppWeekVal.toLocaleString('es-EC', { minimumFractionDigits: 2 })}/caja`, ML + 420, y + 15);
    }
    pdf.text(`Negociaciones: ${filteredEntries.length}`, ML + 620, y + 15);
    y += 32;

    // ── PPP por día (7 cards) ───────────────────────────────────────
    const pppDays = Array.from({ length: 7 }, (_, i) => {
      const dayISO = toISO(addDays(weekStart, i));
      const dayEntries = filteredEntries.filter(e => e.date === dayISO);
      const dayBoxes = dayEntries.reduce((s, e) => s + parseFloat(e.boxes || 0), 0);
      const dayWeightedSum = dayEntries.reduce((s, e) => s + parseFloat(e.boxes || 0) * parseFloat(e.negotiated_price || 0), 0);
      return {
        dayISO,
        label: DAY_NAMES[i],
        short: DAY_NAMES[i].slice(0, 3),
        boxes: dayBoxes,
        ppp: dayBoxes > 0 ? dayWeightedSum / dayBoxes : null,
      };
    });

    const cardW = (CW - 6 * 6) / 7; // 7 cards with 6px gap
    const cardH = 40;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(107, 114, 133);
    pdf.text('PRECIO PROMEDIO PONDERADO POR DÍA', ML, y + 4);
    y += 10;

    pppDays.forEach((d, i) => {
      const cx = ML + i * (cardW + 6);
      if (d.ppp !== null) {
        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(23, 198, 113);
        pdf.setLineWidth(0.8);
      } else {
        pdf.setFillColor(248, 249, 252);
        pdf.setDrawColor(232, 234, 240);
        pdf.setLineWidth(0.4);
      }
      pdf.roundedRect(cx, y, cardW, cardH, 3, 3, 'FD');

      // Day name
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(7);
      pdf.setTextColor(107, 114, 133);
      pdf.text(d.short.toUpperCase(), cx + cardW / 2, y + 12, { align: 'center' });

      if (d.ppp !== null) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(23, 198, 113);
        pdf.text(`$${d.ppp.toLocaleString('es-EC', { minimumFractionDigits: 2 })}`, cx + cardW / 2, y + 26, { align: 'center' });
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(6);
        pdf.setTextColor(155, 165, 188);
        pdf.text('/caja', cx + cardW / 2, y + 34, { align: 'center' });
      } else {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(14);
        pdf.setTextColor(208, 211, 222);
        pdf.text('—', cx + cardW / 2, y + 28, { align: 'center' });
      }
    });
    y += cardH + 14;

    // ── Charts side by side ─────────────────────────────────────────
    const barImg  = await svgToDataUrl(barChartRef.current);
    const lineImg = await svgToDataUrl(lineChartRef.current);
    const chartAreaW = (CW - 12) / 2;

    if (barImg || lineImg) {
      checkPage(230);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(51, 51, 51);
      if (barImg)  pdf.text('Total ($) por día y productor', ML, y + 10);
      if (lineImg) pdf.text('Total de Cajas por día', ML + chartAreaW + 12, y + 10);
      y += 16;

      const maxChartH = 180;

      if (barImg) {
        const ratio = barImg.height / barImg.width;
        const imgH = Math.min(ratio * chartAreaW, maxChartH);
        pdf.addImage(barImg.dataUrl, 'PNG', ML, y, chartAreaW, imgH);
      }
      if (lineImg) {
        const ratio = lineImg.height / lineImg.width;
        const imgH = Math.min(ratio * chartAreaW, maxChartH);
        pdf.addImage(lineImg.dataUrl, 'PNG', ML + chartAreaW + 12, y, chartAreaW, imgH);
      }

      const barH  = barImg  ? Math.min((barImg.height  / barImg.width)  * chartAreaW, maxChartH) : 0;
      const lineH = lineImg ? Math.min((lineImg.height / lineImg.width) * chartAreaW, maxChartH) : 0;
      y += Math.max(barH, lineH) + 18;
    }

    // ── Negociaciones por día de la semana ────────────────────────────
    const cols = [
      { x: ML,        w: 120, label: 'Productor',       align: 'left'  },
      { x: ML + 120,  w: 80,  label: 'Marca',           align: 'left'  },
      { x: ML + 200,  w: 140, label: 'Tipo de Carga',   align: 'left'  },
      { x: ML + 340,  w: 70,  label: 'Cajas',           align: 'right' },
      { x: ML + 410,  w: 100, label: 'Precio Pactado',  align: 'right' },
      { x: ML + 510,  w: 80,  label: 'Descuentos',      align: 'right' },
      { x: ML + 590,  w: 0,   label: 'Total ($)',        align: 'right' },
    ];
    cols[6].w = W - MR - cols[6].x;

    const ROW_H = 17;

    const drawTableHeader = () => {
      pdf.setFillColor(23, 198, 113);
      pdf.setDrawColor(23, 198, 113);
      pdf.rect(ML, y, CW, ROW_H, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(7.5);
      pdf.setTextColor(255, 255, 255);
      cols.forEach(col => {
        const tx = col.align === 'right' ? col.x + col.w - 4 : col.x + 4;
        pdf.text(col.label, tx, y + 11, { align: col.align === 'right' ? 'right' : 'left' });
      });
      y += ROW_H;
    };

    for (let dayIdx = 0; dayIdx < 7; dayIdx++) {
      const dayISO = toISO(addDays(weekStart, dayIdx));
      const dayEntries = filteredEntries.filter(e => e.date === dayISO);

      // Skip empty days
      if (dayEntries.length === 0) continue;

      const dayBoxes = dayEntries.reduce((s, e) => s + parseFloat(e.boxes || 0), 0);
      const dayTotal = dayEntries.reduce((s, e) => s + parseFloat(e.total || 0), 0);
      const dayPPP = pppDays[dayIdx].ppp;

      // Day header bar
      checkPage(ROW_H * 2 + 30);
      pdf.setFillColor(232, 245, 233);
      pdf.setDrawColor(23, 198, 113);
      pdf.setLineWidth(0.8);
      pdf.roundedRect(ML, y, CW, 22, 4, 4, 'FD');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(23, 130, 80);
      pdf.text(`${DAY_NAMES[dayIdx]} – ${formatDate(dayISO)}`, ML + 10, y + 14);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(80, 80, 80);
      const dayStats = `${dayEntries.length} negociaciones  |  ${dayBoxes.toLocaleString('es-EC', { minimumFractionDigits: 0 })} cajas  |  $${dayTotal.toLocaleString('es-EC', { minimumFractionDigits: 2 })}`;
      pdf.text(dayStats, ML + 300, y + 14);
      if (dayPPP !== null) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.setTextColor(21, 101, 192);
        pdf.text(`PPP: $${dayPPP.toLocaleString('es-EC', { minimumFractionDigits: 2 })}/caja`, W - MR - 10, y + 14, { align: 'right' });
      }
      y += 28;

      // Table header
      drawTableHeader();

      // Day entries rows
      dayEntries.forEach((e, idx) => {
        checkPage(ROW_H + 2);
        if (idx % 2 === 1) {
          pdf.setFillColor(245, 247, 250);
          pdf.rect(ML, y, CW, ROW_H, 'F');
        }

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7.5);
        pdf.setTextColor(51, 51, 51);

        const loadTypeLabel = LOAD_TYPES.find(lt => lt.value === e.load_type)?.label || e.load_type || '';
        const rowValues = [
          e.producer_display || 'Sin nombre',
          e.marca_display || e.marca || '',
          loadTypeLabel,
          parseFloat(e.boxes || 0).toLocaleString('es-EC'),
          `$${parseFloat(e.negotiated_price || 0).toLocaleString('es-EC', { minimumFractionDigits: 2 })}`,
          `$${parseFloat(e.discounts || 0).toLocaleString('es-EC', { minimumFractionDigits: 2 })}`,
          `$${parseFloat(e.total || 0).toLocaleString('es-EC', { minimumFractionDigits: 2 })}`,
        ];

        cols.forEach((col, ci) => {
          const tx = col.align === 'right' ? col.x + col.w - 4 : col.x + 4;
          let text = rowValues[ci];
          while (pdf.getTextWidth(text) > col.w - 8 && text.length > 1) {
            text = text.slice(0, -1);
          }
          if (ci === 6) {
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(23, 130, 80);
          } else {
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(51, 51, 51);
          }
          pdf.text(text, tx, y + 11, { align: col.align === 'right' ? 'right' : 'left' });
        });

        pdf.setDrawColor(232, 234, 240);
        pdf.setLineWidth(0.3);
        pdf.line(ML, y + ROW_H, W - MR, y + ROW_H);
        y += ROW_H;
      });

      // Day subtotal row
      checkPage(ROW_H + 4);
      pdf.setFillColor(248, 253, 248);
      pdf.setDrawColor(23, 198, 113);
      pdf.setLineWidth(0.5);
      pdf.rect(ML, y, CW, ROW_H, 'FD');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(7.5);
      pdf.setTextColor(23, 130, 80);
      pdf.text(`Subtotal ${DAY_NAMES[dayIdx]}`, ML + 4, y + 11);
      pdf.text(
        dayBoxes.toLocaleString('es-EC', { minimumFractionDigits: 2 }),
        cols[3].x + cols[3].w - 4, y + 11, { align: 'right' }
      );
      pdf.text(
        `$${dayTotal.toLocaleString('es-EC', { minimumFractionDigits: 2 })}`,
        cols[6].x + cols[6].w - 4, y + 11, { align: 'right' }
      );
      y += ROW_H + 12;
    }

    // ── Grand totals row ─────────────────────────────────────────────
    checkPage(ROW_H + 8);
    pdf.setFillColor(23, 198, 113);
    pdf.setDrawColor(23, 198, 113);
    pdf.setLineWidth(1);
    pdf.roundedRect(ML, y, CW, 22, 4, 4, 'FD');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(255, 255, 255);
    pdf.text('TOTAL GENERAL', ML + 10, y + 14);
    pdf.text(
      `${parseFloat(totalCajas).toLocaleString('es-EC', { minimumFractionDigits: 2 })} cajas`,
      ML + 360, y + 14, { align: 'right' }
    );
    pdf.text(
      `$${parseFloat(totalMonto).toLocaleString('es-EC', { minimumFractionDigits: 2 })}`,
      W - MR - 10, y + 14, { align: 'right' }
    );
    if (pppWeekVal !== null) {
      pdf.text(
        `PPP: $${pppWeekVal.toLocaleString('es-EC', { minimumFractionDigits: 2 })}/caja`,
        ML + 500, y + 14
      );
    }
    y += 30;

    // ── Footer ───────────────────────────────────────────────────────
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7);
      pdf.setTextColor(160, 160, 160);
      pdf.text('BanaPrecios.com – Reporte generado automáticamente', ML, H - 16);
      pdf.text(`Página ${i} de ${pageCount}`, W - MR, H - 16, { align: 'right' });
    }

    pdf.save('comercializacion.pdf');
  };

  const totalCajas = entries.reduce((s, e) => s + parseFloat(e.boxes || 0), 0);
  const totalMonto = entries.reduce((s, e) => s + parseFloat(e.total || 0), 0);

  // ─── Precio promedio ponderado ────────────────────────────────────────────────
  // PPP = Σ(cajas_i × precio_i) / Σ(cajas_i)
  const pppWeekNum = entries.reduce((s, e) => s + parseFloat(e.boxes || 0) * parseFloat(e.negotiated_price || 0), 0);
  const pppWeek = totalCajas > 0 ? pppWeekNum / totalCajas : null;

  const pppByDay = Array.from({ length: 7 }, (_, i) => {
    const dayISO = toISO(addDays(weekStart, i));
    const dayEntries = entries.filter(e => e.date === dayISO);
    const dayBoxes = dayEntries.reduce((s, e) => s + parseFloat(e.boxes || 0), 0);
    const dayWeightedSum = dayEntries.reduce((s, e) => s + parseFloat(e.boxes || 0) * parseFloat(e.negotiated_price || 0), 0);
    return {
      dayISO,
      label: DAY_NAMES[i],
      short: DAY_NAMES[i].slice(0, 3),
      boxes: dayBoxes,
      ppp: dayBoxes > 0 ? dayWeightedSum / dayBoxes : null,
    };
  });

  // ─── Datos del gráfico ───────────────────────────────────────────────────────
  const CHART_COLORS = ['#17c671','#2962ff','#fd7e14','#e83e8c','#6f42c1','#17a2b8','#ffc107','#28a745'];

  const chartProducers = [...new Set(entries.map(e => e.producer_display || 'Sin nombre'))];

  const chartData = Array.from({ length: 7 }, (_, i) => {
    const dayISO = toISO(addDays(weekStart, i));
    const dayEntries = entries.filter(e => e.date === dayISO);
    const row = { day: DAY_NAMES[i].slice(0, 3) };
    chartProducers.forEach(prod => {
      row[prod] = parseFloat(
        dayEntries
          .filter(e => (e.producer_display || 'Sin nombre') === prod)
          .reduce((s, e) => s + parseFloat(e.total || 0), 0)
          .toFixed(2)
      );
    });
    return row;
  });

  const chartDataBoxes = Array.from({ length: 7 }, (_, i) => {
    const dayISO = toISO(addDays(weekStart, i));
    const total = entries
      .filter(e => e.date === dayISO)
      .reduce((s, e) => s + parseFloat(e.boxes || 0), 0);
    return { day: DAY_NAMES[i].slice(0, 3), total: parseFloat(total.toFixed(0)) };
  });

  const hasChartData = entries.length > 0;

  const entriesByDay = {};
  for (let i = 0; i < 7; i++) {
    const dayISO = toISO(addDays(weekStart, i));
    entriesByDay[dayISO] = entries.filter(e => e.date === dayISO);
  }

  return (
    <div className="wc-container">

      {/* ── Selector de área ── */}
      {sharedWithMe.length > 0 && (
        <div className="wc-area-selector">
          <span className="wc-area-label">{t('wc_viewing_area')}</span>
          <select
            value={viewingOwner || '__mine__'}
            onChange={e => setViewingOwner(e.target.value === '__mine__' ? null : e.target.value)}
          >
            <option value="__mine__">{t('wc_my_area')}</option>
            {sharedWithMe.map(a => (
              <option key={a.owner_username} value={a.owner_username}>
                {a.owner_username}
              </option>
            ))}
          </select>
          {isReadOnly && <span className="wc-readonly-badge">{t('wc_readonly')}</span>}
        </div>
      )}

      {/* ── Cabecera semana ── */}
      <div className="wc-header">
        <div className="wc-week-nav">
          <button className="wc-nav-btn" onClick={prevWeek}>&#8592;</button>
          <div className="wc-week-label">
            <span className="wc-week-number">{t('week')} {weekNumber}</span>
            <span className="wc-week-dates">{weekLabel}</span>
          </div>
          <button className="wc-nav-btn" onClick={nextWeek}>&#8594;</button>
          <button className="wc-today-btn" onClick={currentWeek}>{t('wc_today')}</button>
        </div>

        <div className="wc-actions">
          {!isReadOnly && (
            <>
              <button className="wc-btn wc-btn-share" onClick={() => { setShowSharePanel(v => !v); fetchFollowingSuggestions(); fetchSharedUsers(); }}>
                {t('wc_share')}
              </button>
              <button className="wc-btn wc-btn-manage" onClick={() => setShowManage(v => !v)}>
                {t('wc_manage')}
              </button>
            </>
          )}
          <button className="wc-btn wc-btn-export" onClick={() => setShowExport(v => !v)}>
            {t('wc_export')}
          </button>
        </div>
      </div>

      {/* ── Totales semana ── */}
      <div className="wc-totals-bar">
        <span>{t('wc_total_boxes')} <strong>{formatCurrency(totalCajas)}</strong></span>
        <span>{t('wc_total_amount')} <strong>${formatCurrency(totalMonto)}</strong></span>
        {pppWeek !== null && (
          <span className="wc-totals-ppp">
            {t('wc_ppp_week')} <strong>${formatCurrency(pppWeek)}/{t('wc_per_box')}</strong>
          </span>
        )}
      </div>

      {/* ── PPP por día ── */}
      {hasChartData && (
        <div className="wc-ppp-row">
          {pppByDay.map(d => (
            <div key={d.dayISO} className={`wc-ppp-card${d.ppp !== null ? ' wc-ppp-card--active' : ''}`}>
              <span className="wc-ppp-day">{d.short}</span>
              {d.ppp !== null ? (
                <>
                  <span className="wc-ppp-value">${formatCurrency(d.ppp)}</span>
                  <span className="wc-ppp-label">{t('wc_per_box')}</span>
                </>
              ) : (
                <span className="wc-ppp-empty">—</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Dashboard expandido: KPIs por productor ── */}
      {hasChartData && (() => {
        const producerStats = {};
        entries.forEach(e => {
          const name = e.producer_display || t('wc_unnamed');
          if (!producerStats[name]) producerStats[name] = { boxes: 0, total: 0, count: 0, weightedSum: 0 };
          const boxes = parseFloat(e.boxes || 0);
          const price = parseFloat(e.negotiated_price || 0);
          producerStats[name].boxes += boxes;
          producerStats[name].total += parseFloat(e.total || 0);
          producerStats[name].count += 1;
          producerStats[name].weightedSum += boxes * price;
        });
        const topProducers = Object.entries(producerStats)
          .map(([name, s]) => ({ name, ...s, ppp: s.boxes > 0 ? s.weightedSum / s.boxes : 0 }))
          .sort((a, b) => b.total - a.total);
        const topByVolume = [...topProducers].sort((a, b) => b.boxes - a.boxes)[0];
        const topByAmount = topProducers[0];
        const avgPPP = pppWeek;

        return (
          <div className="wc-dashboard">
            <div className="wc-dashboard-title">{t('wc_dashboard_title')}</div>
            <div className="wc-kpi-row">
              <div className="wc-kpi-card">
                <span className="wc-kpi-label">{t('wc_kpi_negotiations')}</span>
                <span className="wc-kpi-value">{entries.length}</span>
              </div>
              <div className="wc-kpi-card">
                <span className="wc-kpi-label">{t('wc_kpi_producers')}</span>
                <span className="wc-kpi-value">{topProducers.length}</span>
              </div>
              <div className="wc-kpi-card">
                <span className="wc-kpi-label">{t('wc_kpi_top_volume')}</span>
                <span className="wc-kpi-value wc-kpi-value--highlight">{topByVolume?.name || '—'}</span>
                <span className="wc-kpi-sub">{topByVolume ? `${formatCurrency(topByVolume.boxes)} cajas` : ''}</span>
              </div>
              <div className="wc-kpi-card">
                <span className="wc-kpi-label">{t('wc_kpi_top_amount')}</span>
                <span className="wc-kpi-value wc-kpi-value--highlight">{topByAmount?.name || '—'}</span>
                <span className="wc-kpi-sub">{topByAmount ? `$${formatCurrency(topByAmount.total)}` : ''}</span>
              </div>
              <div className="wc-kpi-card">
                <span className="wc-kpi-label">{t('wc_kpi_avg_ppp')}</span>
                <span className="wc-kpi-value wc-kpi-value--green">{avgPPP !== null ? `$${formatCurrency(avgPPP)}` : '—'}</span>
                <span className="wc-kpi-sub">{t('wc_per_box')}</span>
              </div>
            </div>

            {topProducers.length > 1 && (
              <div className="wc-top-table">
                <div className="wc-top-table-title">{t('wc_ranking_producers')}</div>
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('wc_producer')}</th>
                      <th className="wc-th-right">{t('wc_boxes')}</th>
                      <th className="wc-th-right">{t('wc_total')}</th>
                      <th className="wc-th-right">PPP</th>
                      <th className="wc-th-right">{t('wc_kpi_negotiations')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topProducers.map((p, i) => (
                      <tr key={p.name}>
                        <td className="wc-rank">{i + 1}</td>
                        <td className="wc-producer-name">{p.name}</td>
                        <td className="wc-td-right">{formatCurrency(p.boxes)}</td>
                        <td className="wc-td-right wc-td-bold">${formatCurrency(p.total)}</td>
                        <td className="wc-td-right wc-td-green">${formatCurrency(p.ppp)}</td>
                        <td className="wc-td-right">{p.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Gráficos semanales ── */}
      {hasChartData && (
        <div className="wc-charts-row">
          <div className="wc-chart-section" ref={barChartRef}>
            <div className="wc-chart-title">{t('wc_chart_total_daily')}</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 12, right: 16, left: 0, bottom: 4 }} barCategoryGap="22%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f1f5" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: '#6b7285', fontSize: 12, fontWeight: 600 }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={v => `$${v.toLocaleString('es-EC')}`} tick={{ fill: '#9ba5bc', fontSize: 11 }} axisLine={false} tickLine={false} width={76} />
                <Tooltip
                  cursor={{ fill: '#f4f6fb' }}
                  formatter={(value, name) => [`$${parseFloat(value).toLocaleString('es-EC', { minimumFractionDigits: 2 })}`, name]}
                  contentStyle={{
                    background: '#fff',
                    border: '1px solid #e8eaf0',
                    borderRadius: 10,
                    fontSize: 12,
                    boxShadow: '0 6px 20px rgba(28,33,54,0.12)',
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11, color: '#6b7285' }} />
                {chartProducers.map((prod, i) => (
                  <Bar
                    key={prod}
                    dataKey={prod}
                    stackId="a"
                    fill={CHART_COLORS[i % CHART_COLORS.length]}
                    radius={i === chartProducers.length - 1 ? [8, 8, 0, 0] : [0, 0, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="wc-chart-section" ref={lineChartRef}>
            <div className="wc-chart-title">{t('wc_chart_boxes_daily')}</div>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chartDataBoxes} margin={{ top: 12, right: 16, left: 0, bottom: 4 }}>
                <defs>
                  <linearGradient id="wcLineGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#17c671" stopOpacity={0.32} />
                    <stop offset="100%" stopColor="#17c671" stopOpacity={0}    />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f1f5" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: '#6b7285', fontSize: 12, fontWeight: 600 }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={v => v.toLocaleString('es-EC')} tick={{ fill: '#9ba5bc', fontSize: 11 }} axisLine={false} tickLine={false} width={52} />
                <Tooltip
                  cursor={{ stroke: '#9ba5bc', strokeDasharray: '3 3' }}
                  formatter={(value) => [`${parseFloat(value).toLocaleString('es-EC')} cajas`, 'Total']}
                  contentStyle={{
                    background: '#fff',
                    border: '1px solid #e8eaf0',
                    borderRadius: 10,
                    fontSize: 12,
                    boxShadow: '0 6px 20px rgba(28,33,54,0.12)',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="total"
                  name="Cajas"
                  stroke="#17c671"
                  strokeWidth={3}
                  dot={{ r: 5, fill: '#17c671', strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 7, fill: '#17c671' }}
                  fill="url(#wcLineGrad)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── Panel compartir ── */}
      {showSharePanel && !isReadOnly && (
        <div className="wc-panel">
          <h3>{t('wc_shared_users')}</h3>
          <form className="wc-share-form" onSubmit={handleShare} style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder={t('wc_search_username')}
              value={shareUsername}
              onChange={e => {
                const val = e.target.value;
                setShareUsername(val);
                setShowSuggestions(true);
                clearTimeout(shareDebounceRef.current);
                shareDebounceRef.current = setTimeout(() => searchUsers(val.trim()), 300);
              }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              autoComplete="off"
              required
            />
            <button type="submit" className="wc-btn wc-btn-save">{t('wc_add')}</button>
            {showSuggestions && (
              <ul className="wc-suggestions">
                {(shareUsername.length >= 2 ? searchResults : followingSuggestions
                    .filter(u =>
                      shareUsername.length === 0 ||
                      u.username.toLowerCase().includes(shareUsername.toLowerCase()) ||
                      (u.full_name || '').toLowerCase().includes(shareUsername.toLowerCase())
                    )
                  )
                  .slice(0, 6)
                  .map(u => (
                    <li
                      key={u.username}
                      className="wc-suggestion-item"
                      onMouseDown={() => {
                        setShareUsername(u.username);
                        setShowSuggestions(false);
                      }}
                    >
                      {u.photo_url && <img src={u.photo_url} alt="" className="wc-suggestion-avatar" />}
                      <span>@{u.username}</span>
                      {u.full_name && <span className="wc-suggestion-name">{u.full_name}</span>}
                    </li>
                  ))
                }
              </ul>
            )}
          </form>
          {shareMsg && <p className="wc-share-msg">{shareMsg}</p>}
          <ul className="wc-shared-list">
            {sharedUsers.map(u => (
              <li key={u.id}>
                {u.shared_with_photo && <img src={u.shared_with_photo} alt="" className="wc-avatar" />}
                <span>@{u.shared_with_username}</span>
                <button className="wc-btn-revoke" onClick={() => handleRevokeShare(u.id)}>{t('wc_revoke')}</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Panel gestionar productores y marcas ── */}
      {showManage && !isReadOnly && (
        <div className="wc-panel">
          <h3>{t('wc_manage_producers_brands')}</h3>
          <div className="wc-manage-cols">

            {/* Productores */}
            <div>
              <div className="wc-manage-subtitle">{t('wc_producers')}</div>
              {producers.length === 0 && <p className="wc-manage-empty">{t('wc_no_producers')}</p>}
              <ul className="wc-manage-list">
                {producers.map(p => (
                  <li key={p.id} className="wc-manage-item">
                    {editingProducer?.id === p.id ? (
                      <>
                        <input
                          className="wc-manage-input"
                          value={editingProducer.name}
                          onChange={e => setEditingProducer(ep => ({ ...ep, name: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') handleRenameProducer(p.id, editingProducer.name); if (e.key === 'Escape') setEditingProducer(null); }}
                          autoFocus
                        />
                        <button className="wc-manage-btn wc-manage-save" onClick={() => handleRenameProducer(p.id, editingProducer.name)}>✓</button>
                        <button className="wc-manage-btn wc-manage-cancel" onClick={() => setEditingProducer(null)}>✕</button>
                      </>
                    ) : (
                      <>
                        <span className="wc-manage-name">{p.name}</span>
                        <button className="wc-manage-btn wc-manage-edit" onClick={() => setEditingProducer({ id: p.id, name: p.name })}>{t('wc_edit')}</button>
                        <button className="wc-manage-btn wc-manage-del" onClick={() => handleDeleteProducer(p.id)}>{t('wc_delete')}</button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Marcas */}
            <div>
              <div className="wc-manage-subtitle">{t('wc_brands')}</div>
              {marcas.length === 0 && <p className="wc-manage-empty">{t('wc_no_brands')}</p>}
              <ul className="wc-manage-list">
                {marcas.map(m => (
                  <li key={m.id} className="wc-manage-item">
                    {editingMarca?.id === m.id ? (
                      <>
                        <input
                          className="wc-manage-input"
                          value={editingMarca.name}
                          onChange={e => setEditingMarca(em => ({ ...em, name: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') handleRenameMarca(m.id, editingMarca.name); if (e.key === 'Escape') setEditingMarca(null); }}
                          autoFocus
                        />
                        <button className="wc-manage-btn wc-manage-save" onClick={() => handleRenameMarca(m.id, editingMarca.name)}>✓</button>
                        <button className="wc-manage-btn wc-manage-cancel" onClick={() => setEditingMarca(null)}>✕</button>
                      </>
                    ) : (
                      <>
                        <span className="wc-manage-name">{m.name}</span>
                        <button className="wc-manage-btn wc-manage-edit" onClick={() => setEditingMarca({ id: m.id, name: m.name })}>{t('wc_edit')}</button>
                        <button className="wc-manage-btn wc-manage-del" onClick={() => handleDeleteMarca(m.id)}>{t('wc_delete')}</button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      )}

      {/* ── Panel exportar ── */}
      {showExport && (
        <div className="wc-panel">
          <h3>{t('wc_export_records')}</h3>
          <p className="wc-export-hint">{t('wc_export_hint')}</p>
          <div className="wc-export-dates">
            <label>{t('from')} <input type="date" value={exportStart} onChange={e => setExportStart(e.target.value)} /></label>
            <label>{t('to')} <input type="date" value={exportEnd} onChange={e => setExportEnd(e.target.value)} /></label>
          </div>
          <div className="wc-export-btns">
            <button className="wc-btn wc-btn-excel" onClick={() => handleExport('excel')}>{t('download_excel')}</button>
            <button className="wc-btn wc-btn-pdf" onClick={() => handleExport('pdf')}>{t('download_pdf')}</button>
          </div>
        </div>
      )}

      {/* ── Días de la semana ── */}
      {loading ? (
        <p className="wc-loading">{t('loading_dots')}</p>
      ) : (
        <div className="wc-days">
          {Array.from({ length: 7 }, (_, i) => {
            const dayDate = addDays(weekStart, i);
            const dayISO = toISO(dayDate);
            const dayEntries = entriesByDay[dayISO] || [];
            const isToday = toISO(new Date()) === dayISO;

            return (
              <div key={dayISO} className={`wc-day${isToday ? ' wc-day--today' : ''}`}>
                <div className="wc-day-header">
                  <span className="wc-day-name">{DAY_NAMES[i]}</span>
                  <span className="wc-day-date">{formatDate(dayISO)}</span>
                  {!isReadOnly && (
                    <button className="wc-add-btn" onClick={() => openNew(dayISO)}>+ Agregar</button>
                  )}
                </div>

                {dayEntries.length > 0 && (
                  <div className="wc-entries">
                    {dayEntries.map(entry => (
                      <div key={entry.id} className="wc-entry">
                        <div className="wc-entry-top">
                          <span className="wc-entry-producer">{entry.producer_display || '–'}</span>
                          <span className="wc-entry-load">{entry.load_type_display || '–'}</span>
                        </div>
                        <div className="wc-entry-mid">
                          {entry.marca && <span className="wc-entry-marca">{entry.marca}</span>}
                        </div>
                        <div className="wc-entry-numbers">
                          <span>{formatCurrency(entry.boxes)} cajas</span>
                          <span>${formatCurrency(entry.negotiated_price)}</span>
                          {parseFloat(entry.discounts) > 0 && (
                            <span className="wc-entry-disc">-${formatCurrency(entry.discounts)}</span>
                          )}
                          <span className="wc-entry-total">=${formatCurrency(entry.total)}</span>
                        </div>
                        {entry.photo_url && (
                          <a href={entry.photo_url} target="_blank" rel="noreferrer" className="wc-photo-link">
                            Ver foto respaldo
                          </a>
                        )}
                        {entry.notes && <p className="wc-entry-notes">{entry.notes}</p>}
                        {!isReadOnly && (
                          <div className="wc-entry-actions">
                            <button className="wc-btn-edit" onClick={() => openEdit(entry)}>{t('wc_edit')}</button>
                            <button className="wc-btn-del" onClick={() => handleDelete(entry.id)}>{t('wc_delete')}</button>
                          </div>
                        )}
                      </div>
                    ))}
                    <div className="wc-day-subtotal">
                      Subtotal: <strong>{formatCurrency(dayEntries.reduce((s, e) => s + parseFloat(e.boxes || 0), 0))} cajas</strong>
                      {' · '}
                      <strong>${formatCurrency(dayEntries.reduce((s, e) => s + parseFloat(e.total || 0), 0))}</strong>
                    </div>
                  </div>
                )}

                {dayEntries.length === 0 && (
                  <p className="wc-empty-day">{t('wc_no_records')}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Modal de entrada ── */}
      {showModal && !isReadOnly && (
        <div className="wc-modal-overlay" onClick={closeModal}>
          <div className="wc-modal" onClick={e => e.stopPropagation()}>
            <div className="wc-modal-header">
              <h3>{editEntry ? t('wc_edit_entry') : t('wc_new_entry')} – {formatDate(formDay)}</h3>
              <button className="wc-modal-close" onClick={closeModal}>✕</button>
            </div>

            <form onSubmit={handleSave} className="wc-form">
              <div className="wc-field">
                <label>{t('wc_producer')}</label>
                <select value={form.producer || '__new__'} onChange={handleProducerSelect}>
                  <option value="__new__">{t('wc_write_new')}</option>
                  {producers.map(p => (
                    <option key={p.id} value={String(p.id)}>{p.name}</option>
                  ))}
                </select>
                {!form.producer && (
                  <input
                    type="text"
                    placeholder={t('wc_producer_name_placeholder')}
                    value={form.producer_name}
                    onChange={e => setForm(f => ({ ...f, producer_name: e.target.value }))}
                    className="wc-input-mt"
                  />
                )}
              </div>

              <div className="wc-field">
                <label>{t('wc_brand')}</label>
                {marcas.length > 0 && marcaMode === 'select' ? (
                  <select
                    value={form.marca}
                    onChange={e => {
                      if (e.target.value === '__new__') {
                        setMarcaMode('new');
                        setForm(f => ({ ...f, marca: '' }));
                      } else {
                        setForm(f => ({ ...f, marca: e.target.value }));
                      }
                    }}
                  >
                    <option value="">{t('wc_select_brand')}</option>
                    {marcas.map(m => (
                      <option key={m.id} value={m.name}>{m.name}</option>
                    ))}
                    <option value="__new__">{t('wc_write_new_brand')}</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    placeholder={t('wc_brand_placeholder')}
                    value={form.marca}
                    onChange={e => setForm(f => ({ ...f, marca: e.target.value }))}
                  />
                )}
                {marcas.length > 0 && marcaMode === 'new' && (
                  <button
                    type="button"
                    style={{ marginTop: 4, fontSize: 11, color: '#6c757d', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    onClick={() => { setMarcaMode('select'); setForm(f => ({ ...f, marca: '' })); }}
                  >
                    {t('wc_select_existing')}
                  </button>
                )}
              </div>

              <div className="wc-field">
                <label>{t('wc_load_type')}</label>
                <select value={form.load_type} onChange={e => setForm(f => ({ ...f, load_type: e.target.value }))}>
                  <option value="">{t('wc_select')}</option>
                  {LOAD_TYPES.map(lt => (
                    <option key={lt.value} value={lt.value}>{lt.label}</option>
                  ))}
                </select>
              </div>

              <div className="wc-field-row">
                <div className="wc-field">
                  <label>{t('wc_boxes')}</label>
                  <input type="number" step="0.01" min="0" value={form.boxes}
                    onChange={e => setForm(f => ({ ...f, boxes: e.target.value }))} placeholder="0" required />
                </div>
                <div className="wc-field">
                  <label>{t('wc_negotiated_price')}</label>
                  <input type="number" step="0.0001" min="0" value={form.negotiated_price}
                    onChange={e => setForm(f => ({ ...f, negotiated_price: e.target.value }))} placeholder="0.00" required />
                </div>
                <div className="wc-field">
                  <label>{t('wc_discounts')}</label>
                  <input type="number" step="0.01" min="0" value={form.discounts}
                    onChange={e => setForm(f => ({ ...f, discounts: e.target.value }))} placeholder="0.00" />
                </div>
              </div>

              <div className="wc-total-preview">
                {t('wc_total')} <strong>${formatCurrency(calcTotal())}</strong>
                <span className="wc-formula"> ({form.boxes || 0} × ${form.negotiated_price || 0} − ${form.discounts || 0})</span>
              </div>

              <div className="wc-field">
                <label>{t('wc_notes')}</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2} placeholder={t('wc_notes_placeholder')} />
              </div>

              <div className="wc-field">
                <label>{t('wc_photo')}</label>
                {editEntry?.photo_url && (
                  <a href={editEntry.photo_url} target="_blank" rel="noreferrer" className="wc-current-photo">{t('wc_view_current_photo')}</a>
                )}
                <input type="file" accept="image/*" onChange={e => setPhotoFile(e.target.files[0] || null)} />
              </div>

              <div className="wc-form-actions">
                <button type="button" className="wc-btn wc-btn-cancel" onClick={closeModal}>{t('cancel')}</button>
                <button type="submit" className="wc-btn wc-btn-save" disabled={saving || photoUploading}>
                  {saving ? t('saving') : photoUploading ? t('wc_uploading_photo') : t('wc_save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
